import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faYoutube, faTiktok } from '@fortawesome/free-brands-svg-icons';
import {
  StyledFooter,
  FooterContainer,
  FooterLogo,
  FooterIcons,
  StyledLink,
  StyledIcon,
  FooterContent,
  FooterColumn,
  ColumnTitle,
  ColumnLinks,
  MobileFooter,
  DesktopFooter,
  StyledCollapse
} from './styled';
import Logo from '../../assets/images/sparkle-logo-single.png';
import { ROUTE_LEGAL_PATH, ROUTE_PATH } from '../../constants/routePath';
import shopService from '../../services/shop/service';

const Footer = () => {
  const [activeKey, setActiveKey] = useState(['']);
  const [readyMadeCategories, setReadyMadeCategories] = useState([]);
  // Fetch ready-made categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await shopService.fetchReadyMadeCategories();
      if (response && Array.isArray(response)) {
        // Limit to 6 categories and format for footer links
        const formattedCategories = response.slice(0, 6).map(category => ({
          label: category.name,
          path: `/shop/${category.slug || category.name.toLowerCase().replace(/\s+/g, '-')}`
        }));
        setReadyMadeCategories(formattedCategories);
      }
    } catch (error) {
      console.error('Error fetching ready-made categories for footer:', error);
      // Keep empty array on error - footer will show only "All Products"
      setReadyMadeCategories([]);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const discoverItems = [
    { label: 'Sparkle for Instagram', path: '/creator/link-in-bio-for-instagram' },
    { label: 'Sparkle for TikTok', path: '/creator/link-in-bio-for-tiktok' },
    { label: 'Sparkle for Twitter', path: '/creator/link-in-bio-for-twitter' }
  ];
  const toolsItems = [
    { label: 'Business Name Generator', path: '/tools/business-name-generator' },
    { label: 'Domain Name Generator', path: '/tools/domain-name-generator' },
    { label: 'AI Image Generator', path: '/tools/ai-image-generator' },
    { label: 'Profit Margin Calculator', path: '/tools/profit-margin-calculator' },
    { label: 'Business Loan Calculator', path: '/tools/business-loan-calculator' },
    { label: 'Design Library', path: '/design-library' }
  ];
  const shopItems = [
    { label: 'All Products', path: '/shop' },
    ...readyMadeCategories
  ];

  const createItems = [
    { label: 'Custom Products', path: '/custom-products' },
    { label: 'Custom T-Shirts', path: '/custom-t-shirts' }
  ];

  const legalItems = [
    { label: 'Legal', path: ROUTE_LEGAL_PATH.LEGAL },
    { label: 'Privacy Policy', path: ROUTE_LEGAL_PATH.PRIVACY_POLICY },
    { label: 'Terms of Service', path: ROUTE_LEGAL_PATH.TERMS_OF_SERVICE },
    { label: 'Creator Login', href: 'https://admin.sparkle.store/auth/login', external: true },
    { label: 'Customer Login', path: ROUTE_PATH.CUSTOMER_LOGIN }
  ];

  const collapseItems = [
    {
      key: '1',
      label: 'Discover', children: (
        <div>
          {discoverItems.map((item, index) => (
            <Link key={index} to={item.path} style={{ textDecoration: 'none', display: 'block', marginBottom: '12px' }}>
              <StyledLink as="span">{item.label}</StyledLink>
            </Link>
          ))}
        </div>
      ),
    }, {
      key: '2',
      label: 'Tools',
      children: (
        <div>
          {toolsItems.map((item, index) => (
            <Link key={index} to={item.path} style={{ textDecoration: 'none', display: 'block', marginBottom: '12px' }}>
              <StyledLink as="span">{item.label}</StyledLink>
            </Link>
          ))}
        </div>
      ),
    }, {
      key: '3',
      label: 'Shop',
      children: (
        <div>
          {shopItems.map((item, index) => (
            <Link key={index} to={item.path} style={{ textDecoration: 'none', display: 'block', marginBottom: '12px' }}>
              <StyledLink as="span">{item.label}</StyledLink>
            </Link>
          ))}
        </div>
      ),
    }, {
      key: '4',
      label: 'Create',
      children: (
        <div>
          {createItems.map((item, index) => (
            <Link key={index} to={item.path} style={{ textDecoration: 'none', display: 'block', marginBottom: '12px' }}>
              <StyledLink as="span">{item.label}</StyledLink>
            </Link>
          ))}
        </div>
      ),
    },
    {
      key: '5',
      label: 'Legal & Access',
      children: (<div>
        {legalItems.map((item, index) => (
          item.external ? (
            <StyledLink key={index} href={item.href} target="_blank" style={{ display: 'block', marginBottom: '12px' }}>
              {item.label}
            </StyledLink>
          ) : (
            <Link key={index} to={item.path} style={{ textDecoration: 'none', display: 'block', marginBottom: '12px' }}>
              <StyledLink as="span">{item.label}</StyledLink>
            </Link>
          )
        ))}
      </div>
      ),
    },
  ];
  return (
    <StyledFooter>
      <FooterContainer>
        {/* Header Section with Logo and Icons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <FooterLogo src={Logo} alt="Sparkle Logo" />
          <FooterIcons>
            <StyledLink href="https://instagram.com/sparkle.creators" target="_blank">
              <StyledIcon>
                <FontAwesomeIcon icon={faInstagram} />
              </StyledIcon>
            </StyledLink>
            <StyledLink href="https://www.youtube.com/@Sparkle.Creators" target="_blank">
              <StyledIcon>
                <FontAwesomeIcon icon={faYoutube} />
              </StyledIcon>
            </StyledLink>
            <StyledLink href="https://tiktok.com/@sparkle.creators" target="_blank">
              <StyledIcon>
                <FontAwesomeIcon icon={faTiktok} />
              </StyledIcon>
            </StyledLink>
          </FooterIcons>
        </div>

        {/* Desktop Footer */}
        <DesktopFooter>
          <FooterContent>
            <FooterColumn>
              <ColumnTitle>Discover</ColumnTitle>
              <ColumnLinks>
                {discoverItems.map((item, index) => (
                  <Link key={index} to={item.path} style={{ textDecoration: 'none' }}>
                    <StyledLink as="span">{item.label}</StyledLink>
                  </Link>
                ))}
              </ColumnLinks>
            </FooterColumn>

            <FooterColumn>
              <ColumnTitle>Tools</ColumnTitle>
              <ColumnLinks>
                {toolsItems.map((item, index) => (
                  <Link key={index} to={item.path} style={{ textDecoration: 'none' }}>
                    <StyledLink as="span">{item.label}</StyledLink>
                  </Link>
                ))}
              </ColumnLinks>            </FooterColumn>

            <FooterColumn>
              <ColumnTitle>Shop</ColumnTitle>
              <ColumnLinks>
                {shopItems.map((item, index) => (
                  <Link key={index} to={item.path} style={{ textDecoration: 'none' }}>
                    <StyledLink as="span">{item.label}</StyledLink>
                  </Link>
                ))}
              </ColumnLinks>
            </FooterColumn>

            <FooterColumn>
              <ColumnTitle>Create</ColumnTitle>
              <ColumnLinks>
                {createItems.map((item, index) => (
                  <Link key={index} to={item.path} style={{ textDecoration: 'none' }}>
                    <StyledLink as="span">{item.label}</StyledLink>
                  </Link>
                ))}
              </ColumnLinks>
            </FooterColumn>

            <FooterColumn>
              <ColumnTitle>Legal & Access</ColumnTitle>
              <ColumnLinks>
                {legalItems.map((item, index) => (
                  item.external ? (
                    <StyledLink key={index} href={item.href} target="_blank">
                      {item.label}
                    </StyledLink>
                  ) : (
                    <Link key={index} to={item.path} style={{ textDecoration: 'none' }}>
                      <StyledLink as="span">{item.label}</StyledLink>
                    </Link>
                  )
                ))}
              </ColumnLinks>
            </FooterColumn>
          </FooterContent>
        </DesktopFooter>

        {/* Mobile Footer */}
        <MobileFooter>
          <StyledCollapse
            items={collapseItems}
            activeKey={activeKey}
            onChange={setActiveKey}
            ghost
            style={{ width: '100%' }}
          />
        </MobileFooter>

        {/* Bottom Section */}
        <div style={{
          borderTop: '1px solid #eee',
          marginTop: '40px',
          paddingTop: '20px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}>          <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', flexWrap: 'wrap' }}>
            <Link to={ROUTE_LEGAL_PATH.TERMS_OF_SERVICE} style={{ textDecoration: 'none' }}>
              <StyledLink as="span" style={{ border: 'none', padding: 0 }}>Terms of Service</StyledLink>
            </Link>
            <Link to={ROUTE_LEGAL_PATH.PRIVACY_POLICY} style={{ textDecoration: 'none' }}>
              <StyledLink as="span" style={{ border: 'none', padding: 0 }}>Privacy Policy</StyledLink>
            </Link>
          </div><div style={{ fontSize: '0.875rem', color: '#666', marginTop: '10px' }}>
            © {new Date().getFullYear()} Sparkle. All rights reserved.
          </div>
        </div>
      </FooterContainer>
    </StyledFooter>
  );
};

export default Footer;